// PrimeNG's Aura preset tints highlights, text/outlined button hovers, toasts, tags,
// messages and tabs with `color-mix(in srgb, {token}, transparent N%)`. PrimeNG injects
// that CSS at runtime, so the build-time lowering in scripts/postcss-legacy-css never
// sees it — and Chrome 109 (Windows 7, the shop counter PC) has no color-mix(), so
// those backgrounds simply disappear there (e.g. the selected day in the datepicker).
//
// Mixing a colour with `transparent` in srgb is exactly that colour at (100 - N)%
// alpha, so every such value is rewritten here to a plain rgba() before PrimeNG ever
// sees the preset. Token references resolve the same way PrimeNG resolves them: the
// colour scheme branch the value sits in, then the shared semantic tokens, then the
// primitive palette.

type Tokens = { [key: string]: unknown };

const COLOR_MIX = /color-mix\(\s*in srgb\s*,\s*([^,()]+?)\s*,\s*transparent\s+([\d.]+)%\s*\)/g;
const REF = /^\{([\w.-]+)\}$/;

function lookup(tree: unknown, path: string[]): unknown {
  let node = tree;
  for (const key of path) {
    if (node === null || typeof node !== 'object') return undefined;
    node = (node as Tokens)[key];
  }
  return node;
}

function resolveColor(value: string, preset: Tokens, scheme: 'light' | 'dark', depth = 0): string | undefined {
  const ref = REF.exec(value.trim());
  if (!ref) return value.trim();
  if (depth > 10) return undefined;

  const path = ref[1].split('.');
  const semantic = preset['semantic'] as Tokens | undefined;
  for (const source of [lookup(semantic, ['colorScheme', scheme]), semantic, preset['primitive']]) {
    const hit = lookup(source, path);
    if (typeof hit === 'string') return resolveColor(hit, preset, scheme, depth + 1);
  }
  return undefined;
}

function toRgba(color: string, alpha: number): string | undefined {
  const hex = /^#([\da-f]{3}|[\da-f]{6})$/i.exec(color);
  if (!hex) return undefined;
  const h = hex[1].length === 3 ? [...hex[1]].map((c) => c + c).join('') : hex[1];
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
  return `rgba(${r}, ${g}, ${b}, ${+alpha.toFixed(3)})`;
}

function flatten(node: unknown, preset: Tokens, scheme: 'light' | 'dark', unresolved: string[]): unknown {
  if (typeof node === 'string') {
    return node.replace(COLOR_MIX, (match, color: string, pct: string) => {
      const resolved = resolveColor(color, preset, scheme);
      const rgba = resolved && toRgba(resolved, 1 - parseFloat(pct) / 100);
      if (!rgba) unresolved.push(match);
      return rgba ?? match;
    });
  }
  if (Array.isArray(node)) return node.map((n) => flatten(n, preset, scheme, unresolved));
  if (node && typeof node === 'object') {
    const out: Tokens = {};
    for (const [key, value] of Object.entries(node)) {
      const childScheme = key === 'light' || key === 'dark' ? key : scheme;
      out[key] = flatten(value, preset, childScheme, unresolved);
    }
    return out;
  }
  return node;
}

/**
 * Returns a copy of `preset` with every `color-mix(in srgb, X, transparent N%)` replaced
 * by the equivalent rgba(). `unresolved` lists any it could not rewrite (an unknown
 * token or non-hex colour) — left as-is, and reported by the caller.
 */
export function flattenColorMix<T extends object>(preset: T): { preset: T; unresolved: string[] } {
  const unresolved: string[] = [];
  // Values outside a light/dark branch resolve against dark — the app is dark-only
  // (darkModeSelector '.p-dark' is always on; see app.config.ts).
  const flat = flatten(preset, preset as Tokens, 'dark', unresolved) as T;
  return { preset: flat, unresolved };
}
